import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Award, MapPin, Clock } from 'lucide-react';

const Hero = () => {
  const scrollToContent = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Fixed Background Image (high priority) */}
      <img
        src="/images/IMG_9217.jpg"
        alt="Clinic hero background"
        className="absolute inset-0 w-full h-full object-cover object-[center_20%]"
        loading="eager"
        decoding="sync"
        fetchpriority="high"
        sizes="100vw"
      />
      {/* Overlay */}
      <div className="hero-overlay absolute inset-0"></div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          {/* Main Title */}
          <motion.h1 
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            Dr. Chandra
            <span className="block text-3xl md:text-4xl lg:text-5xl font-medium mt-2">
              Chiropractic & Osteopathy
            </span>
          </motion.h1>

          {/* Experience Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="inline-flex items-center space-x-2 bg-green-600/90 backdrop-blur-sm rounded-full px-6 py-3 mb-6"
          >
            <Award className="w-5 h-5" />
            <span className="font-semibold">6+ Years of Experience | Trained from Sweden, Europe</span>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-xl md:text-2xl font-light mb-8 max-w-2xl mx-auto"
          >
            "Helping you heal naturally – without medicine or surgery."
          </motion.p>

          {/* Location and Hours */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mb-8"
          >
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5" />
              <span>Halduchor near Evergreen School, Haldwani, Nainital</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>Mon-Sat: 9AM-2PM, 4PM-6PM</span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <motion.button
              className="bg-green-600 hover:bg-green-700 text-white font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.querySelector('#appointment');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Book Your Appointment
            </motion.button>
            <motion.button
              className="bg-white hover:bg-gray-50 text-green-600 font-semibold text-lg px-8 py-4 rounded-lg border-2 border-green-600 transition-all duration-300 transform hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.querySelector('#services');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Our Services
            </motion.button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.button
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce"
        onClick={scrollToContent}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        whileHover={{ scale: 1.1 }}
      >
        <ChevronDown className="w-8 h-8" />
      </motion.button>
    </section>
  );
};

export default Hero; 